import { useEffect, useId, useRef, useState } from "react";
import { Platform, ScrollView } from "react-native";
import { Popover, YStack } from "tamagui";
import { useDebounce } from "use-debounce";

import LoadingSpinner from "@/components/animated/LoadingSpinner";
import { Button } from "@/components/Button";
import { Input } from "@/components/forms/Input";
import { Paragraph, Text } from "@/components/Typography";
import { useLiveRegionStore } from "@/store/liveRegion";

export type AutocompleteOption<TData = unknown> = {
  value: string;
  label: string;
  text?: string;
  data?: TData;
};

export type AutocompleteSelectProps<TData = unknown> = {
  value: string;
  displayText: string;
  query: (search: string) => Promise<AutocompleteOption<TData>[]>;
  onSelect: (option: AutocompleteOption<TData>) => void;
  onClear: () => void;
  onBlur?: () => void;
  dataTrackingId: string;
  errorText?: string;
  emptyText?: string;
  minQueryLength?: number;
  label?: string;
  placeholder?: string;
  testID?: string;
  rootProps?: React.ComponentProps<typeof Input.Controlled>["rootProps"];
};

type OptionProps<TData> = {
  id: string;
  option: AutocompleteOption<TData>;
  dataTrackingId: string;
  onSelect: (option: AutocompleteOption<TData>) => void;
  active: boolean;
  onHover: () => void;
  testID?: string;
};

function Option<TData>({
  id,
  option,
  dataTrackingId,
  onSelect,
  active,
  onHover,
  testID,
}: OptionProps<TData>) {
  return (
    <Button.Wrapper
      id={id}
      role="option"
      aria-selected={active}
      tabIndex={-1}
      dataTrackingId={dataTrackingId}
      testID={testID ? `${testID}:${option.value}` : undefined}
      onPress={() => onSelect(option)}
      onHoverIn={onHover}
      width="100%"
      minHeight="$550"
      paddingVertical={0}
      paddingHorizontal="$100"
      justifyContent="flex-start"
      alignItems="center"
      backgroundColor={active ? "$navSelected" : "$transparent"}
      borderRadius={active ? "$75" : undefined}
      hoverStyle={{
        backgroundColor: "$navSelected",
        borderRadius: "$75",
      }}
    >
      <Text>{option.label}</Text>
    </Button.Wrapper>
  );
}

/**
 * Not implemented at this time:
 *   - infinite scroll to load more results via pagination
 *   -
 */
export function AutocompleteSelect<TData = unknown>({
  value,
  displayText,
  query,
  onSelect,
  onClear,
  onBlur,
  dataTrackingId,
  errorText,
  emptyText = "No results found.",
  minQueryLength = 3,
  label,
  placeholder,
  testID,
  rootProps,
}: AutocompleteSelectProps<TData>) {
  const [searchText, setSearchText] = useState(displayText);
  const [selected, setSelected] = useState(!!value);
  const [open, setOpen] = useState(false);
  const [width, setWidth] = useState<number | undefined>(undefined);
  const [options, setOptions] = useState<AutocompleteOption<TData>[]>([]);
  const [resultsFor, setResultsFor] = useState("");
  const focusedRef = useRef(false);
  const externalTextRef = useRef(displayText);
  const queryRef = useRef(query);
  queryRef.current = query;
  const requestRef = useRef(0);
  const [activeIndex, setActiveIndex] = useState(-1);
  const listboxId = useId();
  const optionId = (index: number) => `${listboxId}-${index}`;

  useEffect(() => {
    if (displayText !== externalTextRef.current) {
      externalTextRef.current = displayText;
      if (!focusedRef.current) {
        setSearchText(displayText);
        setSelected(!!value);
      }
    }
  }, [displayText, value]);

  const [debouncedSearch] = useDebounce(searchText.trim(), 300);
  useEffect(() => {
    const enabled =
      open && !selected && debouncedSearch.length >= minQueryLength;
    if (!enabled) {
      return;
    }
    const requestId = ++requestRef.current;
    queryRef
      .current(debouncedSearch)
      .then((results) => {
        if (requestRef.current === requestId) {
          setOptions(results);
          setResultsFor(debouncedSearch);
          setActiveIndex(-1);
        }
      })
      .catch(() => {
        if (requestRef.current === requestId) {
          setOptions([]);
          setResultsFor(debouncedSearch);
          setActiveIndex(-1);
        }
      });
  }, [debouncedSearch, open, selected, minQueryLength]);

  const handleChangeText = (text: string) => {
    setSearchText(text);
    setSelected(false);
    setOpen(true);
    setActiveIndex(-1);
    onClear();
  };

  const handleSelect = (option: AutocompleteOption<TData>) => {
    setSearchText(option.text ?? option.label);
    setSelected(true);
    setOpen(false);
    setActiveIndex(-1);
    onSelect(option);
  };

  const handleBlur = () => {
    focusedRef.current = false;
    onBlur?.();
  };

  const handleKey = (key: string, preventDefault?: () => void) => {
    if (key === "Escape") {
      setOpen(false);
      setActiveIndex(-1);
      return;
    }
    if (!showList) return;
    if (key === "ArrowDown") {
      preventDefault?.();
      setActiveIndex((index) => (index + 1) % options.length);
    } else if (key === "ArrowUp") {
      preventDefault?.();
      setActiveIndex((index) => (index <= 0 ? options.length - 1 : index - 1));
    } else if (key === "Enter" && activeIndex >= 0) {
      preventDefault?.();
      handleSelect(options[activeIndex]);
    }
  };

  const hasQuery = searchText.trim().length >= minQueryLength;
  const resolved =
    searchText.trim() === debouncedSearch && resultsFor === debouncedSearch;
  const showLoading = open && !selected && hasQuery && !resolved;
  const showList = open && !selected && resolved && options.length > 0;
  const showEmpty =
    open && !selected && resolved && hasQuery && options.length === 0;
  const isPopoverOpen = showList || showLoading || showEmpty;

  useEffect(() => {
    if (Platform.OS !== "web" || activeIndex < 0) return;
    document
      .getElementById(`${listboxId}-${activeIndex}`)
      ?.scrollIntoView({ block: "nearest" });
  }, [activeIndex, listboxId]);

  const announcePolite = useLiveRegionStore((s) => s.announcePolite);
  useEffect(() => {
    if (showEmpty) {
      announcePolite(emptyText);
    } else if (showList) {
      announcePolite(
        `${options.length} ${options.length === 1 ? "result" : "results"} available`,
      );
    }
  }, [showEmpty, showList, options.length, emptyText, announcePolite]);

  return (
    <Popover
      open={isPopoverOpen}
      onOpenChange={setOpen}
      placement="bottom-start"
      allowFlip
      offset={0}
    >
      <Popover.Anchor
        width="100%"
        userSelect="text"
        onLayout={(e) => setWidth(e.nativeEvent.layout.width)}
        {...rootProps}
      >
        <Input.Controlled
          role="combobox"
          aria-expanded={isPopoverOpen}
          aria-controls={showList ? listboxId : undefined}
          aria-autocomplete="list"
          aria-haspopup="listbox"
          aria-activedescendant={
            showList && activeIndex >= 0 ? optionId(activeIndex) : undefined
          }
          label={label}
          placeholder={placeholder}
          value={searchText}
          errorText={isPopoverOpen || selected ? undefined : errorText}
          testID={testID}
          onFocus={() => {
            focusedRef.current = true;
            if (!selected) setOpen(true);
          }}
          onBlur={handleBlur}
          onChangeText={handleChangeText}
          {...(Platform.OS === "web"
            ? {
                onKeyDown: (e: React.KeyboardEvent<HTMLDivElement>) =>
                  handleKey(e.key, () => e.preventDefault()),
              }
            : {})}
        />
      </Popover.Anchor>
      <Popover.Content
        width={width}
        alignItems="stretch"
        disableFocusScope
        padding={0}
        elevation="$100"
        borderWidth="$strokeWidth"
        borderColor="$strokeNeutral"
        backgroundColor="$fillModal"
        borderRadius="$150"
        overflow="hidden"
        enterStyle={{ y: -4, opacity: 0 }}
        exitStyle={{ y: -4, opacity: 0 }}
        zIndex={999999}
      >
        {showLoading && (
          <YStack padding="$150" alignItems="center">
            <LoadingSpinner size="$200" />
          </YStack>
        )}
        {showList && (
          <ScrollView
            keyboardShouldPersistTaps="handled"
            style={{ maxHeight: 240 }}
            contentContainerStyle={{ width: "100%" }}
          >
            <YStack
              id={listboxId}
              {...({ role: "listbox" } as object)}
              gap="$50"
              padding="$50"
              width="100%"
            >
              {options.map((option, index) => (
                <Option
                  key={`${option.value}-${option.label}`}
                  id={optionId(index)}
                  option={option}
                  dataTrackingId={dataTrackingId}
                  onSelect={handleSelect}
                  active={index === activeIndex}
                  onHover={() => setActiveIndex(index)}
                  testID={testID}
                />
              ))}
            </YStack>
          </ScrollView>
        )}
        {showEmpty && (
          <Paragraph size="$sm" color="$textSecondary" padding="$150">
            {emptyText}
          </Paragraph>
        )}
      </Popover.Content>
    </Popover>
  );
}
